import { Dialog } from "@headlessui/react";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { PhoneContact, phoneContactSchema } from "../../model/PhoneContact";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useInput } from "../../hooks/useInput";
import { useEffect, useRef, useState } from "react";
import { CheckBoxGroup, CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import FormController from "../../components/fields/FormController";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { Label } from "../../components/select/SelectFields";

export type ContactInformationEditModalProps = {
  phoneConact?: PhoneContact;
  onSubmit: (data: PhoneContact) => Promise<void>;
};

export const ContactInformationEditModal = ({
  phoneConact,
  open,
  toggle,
  closeOnClickOutside,
  onSubmit
}: ModalProp & ContactInformationEditModalProps) => {
  const { control, trigger, handleSubmit, setValue, getValues } =
    useForm<PhoneContact>({
      resolver: yupResolver(phoneContactSchema),
      defaultValues: phoneConact,
    });

  // submit押下フラグ
  const submitProcessing = useRef(false);

  // 曜日選択
  const dayOfWeekItem: CheckBoxItem[] = [
    { id: 'mon', title: '月', value: false },
    { id: 'tue', title: '火', value: false },
    { id: 'wed', title: '水', value: false },
    { id: 'thu', title: '木', value: false },
    { id: 'fri', title: '金', value: false },
    { id: 'sat', title: '土', value: false },
    { id: 'sun', title: '日', value: false },
    { id: 'holiday', title: '祝日', value: false }
  ];
  const [dayOfWeekState, setDayOfWeekState] = useState<CheckBoxItem[]>(dayOfWeekItem);
  const dayOfWeek = useInput(dayOfWeekState);
  useEffect(() => {
    const selectedDay = dayOfWeekState.map((state) => state.value ? "1" : "0")
    setValue('dayOfWeek', selectedDay);
    if(submitProcessing.current === true){
      // 入力チェック
      trigger('dayOfWeek');
    }
  },[dayOfWeekState, setValue, trigger]);

  // 表示状態
  const radioItem: RadioButtonItem[] = [
    { id: "true", title: "表示", value: "true" },
    { id: "false", title: "非表示", value: "false" }
  ];
  const select = useInput(phoneConact?.enableFlag ? "true" : "false");
  useEffect(() => {
    setValue('enableFlag', select.value === "true");
  },[select, setValue]);

  useEffect(() => {
    if(phoneConact !== undefined && phoneConact.id !== ''){
      setValue('id', phoneConact.id);
      setValue('enableFlag', phoneConact.enableFlag);
      setValue('phoneNo', phoneConact.phoneNo);
      setValue('staffName', phoneConact.staffName);
      setValue('dayOfWeek', phoneConact.dayOfWeek);
      setValue('onCallStartTime', phoneConact.onCallStartTime);
      setValue('onCallEndTime', phoneConact.onCallEndTime);
      setValue('createAt', phoneConact.createAt);
      const updateDayOfWeekItem: CheckBoxItem[] = dayOfWeekState.map((state, index) => {
        return {
          id: state.id,
          title: state.title,
          value: phoneConact.dayOfWeek[index] === "1"
        };
      });
      setDayOfWeekState(updateDayOfWeekItem);
      select.setValue(phoneConact.enableFlag ? "true" : "false");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[phoneConact, setValue]);

  const timeSelectList: string[] = [];
  for(let i = 0; i < 2400; i += 15) {
    const time = ('0000' + i).slice(-4);
    timeSelectList.push(`${time.substring(0,2)}:${time.substring(2)}`);
    if(time.lastIndexOf('45', 4) > 0) i += 40;
  };

  const onChangeTime = (e: any) => {
    const id = e.target.name.toString();
    const idDate = new Date(getValues(id));
    if(e.target.value !== null && typeof(e.target.value) === 'string'){
      const value = e.target.value;
      idDate.setHours(Number(value.substring(0, 2)));
      idDate.setMinutes(Number(value.substring(3)));
      setValue(id, idDate);
      setValue(e.target.name, e.target.value);
    }
    if(submitProcessing.current === true){
      // 入力チェック
      trigger(e.target.name);
    }
  };

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className=" relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xltransition-all sm:w-full max-w-screen-md ">
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate>
            <div className="text-center sm:mt-3">
              <div className={"flex items-center"}>
                <Dialog.Title
                  as="h3"
                  className="text-left p-4 text-lg font-medium leading-6 text-gray-900 flex-1"
                >
                  問い合わせ先設定
                </Dialog.Title>
              </div>
              <div className="overflow-y-auto px-8 text-left pb-8 h-fit max-h-[calc(100vh_-_10rem)]">
                <FormBox gridcol={"grid-cols-1"} className={'sm:pt-[0px]'}>
                  <Controller
                    name="enableFlag"
                    control={control}
                    render={({ field, fieldState }) => (
                      <RadioButton
                        {...field}
                        vertical
                        label={'問い合わせボタン表示状態'}
                        children={undefined}
                        value={select}
                        radioButtonItem={radioItem}
                        className={"h-8"}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </FormBox>
                <FormBox>
                  <FormController
                    control={control}
                    name={"phoneNo"}
                    label={"お問い合わせ電話番号"}
                    type={"text"}
                    required
                  />
                  <FormController
                    control={control}
                    name={"staffName"}
                    label={"お問い合わせ担当者名"}
                    type={"text"}
                    required
                  />
                </FormBox>
                <FormBox gridcol={"grid-cols-1"}>
                  <div className={"flex space-x-2 items-center"}>
                    <div className="pb-2 flex">
                      <Label id={"dayOfWeek"}>発信可能曜日</Label>
                      <div className="text-red-500 mx-1">*</div>
                    </div>
                    <Controller
                      name="dayOfWeek"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div className="pb-3">
                          <CheckBoxGroup
                            {...field}
                            children={undefined}
                            checkBoxItem={dayOfWeekState}
                            value={dayOfWeek}
                            vertical
                            setState={setDayOfWeekState}
                            helperText={fieldState.error?.message}
                          />
                          <span role="alert" className="absolute left-0 pl-8 text-xs text-red-500 whitespace-nowrap">{fieldState.error?.message}</span>
                        </div>
                      )}
                    />
                  </div>
                </FormBox>
                <FormBox>
                  <FormController
                    control={control}
                    name={"onCallStartTime"}
                    label={"発信可能開始時間"}
                    type={"time"}
                    step={60}
                    list={'onCallStartTime'}
                    onChange={onChangeTime}
                    required
                  />
                  <datalist id={'onCallStartTime'}>
                    {timeSelectList.map((time, index) => {
                      return (<option key={`onCallStartTime${time}-${index}`} value={time}></option>)
                    })}
                  </datalist>
                  <FormController
                    control={control}
                    name={"onCallEndTime"}
                    label={"発信可能終了時間"}
                    type={"time"}
                    step={60}
                    list={'onCallEndTime'}
                    onChange={onChangeTime}
                    required
                  />
                  <datalist id={'onCallEndTime'}>
                    {timeSelectList.map((time, index) => {
                      return (<option key={`onCallEndTime${time}-${index}`} value={time}></option>)
                    })}
                  </datalist>
                </FormBox>
              </div>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                outline
                onClick={() => {
                  toggle();
                  select.setValue(phoneConact?.enableFlag ? "true" : "false");
                }}
                children={"キャンセル"}
              />
              <Button
                className="mx-2 mt-1 w-[132px]"
                submit
                onClick={() => submitProcessing.current = true}
                children={"登録"}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </Modal>
    </>
  );
};
